/**
 * EmailLogs — /admin/email-logs
 *
 * Wave 6 Batch 3 · Cluster A — Log-Style chrome sweep.
 * Anchor: CreditEngineEvents (Wave 5-Late).
 *
 *   • AdminLayout wrap (unchanged).
 *   • AdminPageHelp lifted out of `actions` (it was mis-nested inside the
 *     top-bar action group).
 *   • `_primitives.Section` shells for filters + events feed.
 *   • `_primitives.SelectInput` / `TextInput` for filters.
 *   • `_primitives.GhostButton` / `AccentButton` for Refresh / CSV.
 *   • STATUS_COLOR pastel map REMOVED. `StatusPill` now emits a DS
 *     `Badge` with `shape="sharp"` + STATUS_TONE map.
 *   • Dense hairline table: `px-4 py-1.5` + `isLast` guard, tabular-nums
 *     on timestamps, font-mono on IDs.
 *   • Detail modal chrome swept to `--ds-*` tokens (backdrop matches
 *     CreditEngineEvents `rgba(27,26,22,0.55)`).
 *
 * Every testid, API call, filter payload preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Download, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";
import { api, formatApiError } from "@/api/client";
import { Section, SelectInput, TextInput, GhostButton, AccentButton } from "@/admin/_primitives";

const STATUS_TONE = {
  sent:    "successMuted",
  sandbox: "warning",
  failed:  "danger",
  bounced: "danger",
};

function StatusPill({ status }) {
  if (!status) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge
      tone={STATUS_TONE[status] || "neutralMuted"}
      size="sm"
      shape="sharp"
      className="uppercase tracking-[0.14em]"
    >
      {status}
    </Badge>
  );
}

function DetailModal({ log, onClose }) {
  if (!log) return null;
  return (
    <div
      data-testid="email-log-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-[var(--ds-radius-card)] p-6 max-h-[85vh] overflow-y-auto"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="email-log-modal-back"
              className="inline-flex items-center gap-1 text-xs transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.18em] mb-1 font-semibold"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                {log.template_key || log.template_alias || "raw"}
              </div>
              <Heading level={2} size="lg" weight="semibold" className="truncate">
                {log.subject || "(no subject)"}
              </Heading>
            </div>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <Meta label="To" mono>{log.to}</Meta>
          <Meta label="Status"><StatusPill status={log.status} /></Meta>
          <Meta label="Message ID" mono muted>{log.message_id || "—"}</Meta>
          <Meta label="Sent at" muted>
            {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
          </Meta>
          {log.template_vars_keys && (
            <Meta label="Template vars used" mono muted>
              {log.template_vars_keys.join(", ")}
            </Meta>
          )}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <div
                className="text-[11px] uppercase tracking-[0.15em] mb-1 font-semibold"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Metadata
              </div>
              <pre
                className="rounded-[var(--ds-radius-input)] p-3 text-[11px] overflow-x-auto font-mono"
                style={{
                  background: "var(--ds-color-admin-surface-muted)",
                  border: "1px solid var(--ds-color-admin-border)",
                  color: "var(--ds-color-ink-muted)",
                }}
              >
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
          {log.error && (
            <div>
              <div
                className="text-[11px] uppercase tracking-[0.15em] mb-1 font-semibold"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Error
              </div>
              <div
                className="rounded-[var(--ds-radius-input)] p-2 text-xs leading-relaxed"
                style={{
                  background: "var(--ds-color-danger-soft)",
                  border: "1px solid var(--ds-color-danger)",
                  color: "var(--ds-color-danger)",
                }}
              >
                {log.error}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, mono, muted, children }) {
  const monoCls = mono ? "font-mono" : "";
  const inkVar = muted ? "var(--ds-color-ink-muted)" : "var(--ds-color-ink)";
  return (
    <div>
      <div
        className="text-[11px] uppercase tracking-[0.15em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div className={`text-sm ${monoCls}`} style={{ color: inkVar }}>
        {children}
      </div>
    </div>
  );
}

export default function AdminEmailLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateFilter, setTemplateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (templateFilter) p.set("template_key", templateFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (search.trim()) p.set("to", search.trim().toLowerCase());
      p.set("limit", "100");
      const { data } = await api.get(`/admin/email-logs?${p.toString()}`);
      setItems(data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [templateFilter, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  async function downloadCsv() {
    try {
      const p = new URLSearchParams();
      if (templateFilter) p.set("template_key", templateFilter);
      if (statusFilter) p.set("status", statusFilter);
      const resp = await api.get(`/admin/email-logs/export/csv?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `email-logs-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AdminLayout
      title="Email Logs"
      subtitle="Every transactional email sent through Postmark"
      actions={
        <div className="flex items-center gap-2">
          <GhostButton data-testid="email-logs-refresh" onClick={load}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </GhostButton>
          <AccentButton data-testid="email-logs-csv" onClick={downloadCsv}>
            <Download className="w-3.5 h-3.5" /> CSV
          </AccentButton>
        </div>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Email Logs"
          purpose="Postmark delivery log — every outbound transactional email and its final delivery state."
          configures="Nothing — read-only. Filter by recipient, template, and status."
          impact="No user-facing impact. Diagnose bounces, spam complaints, and delivery failures."
          safety="Read-only. Log entries expire per Postmark retention policy."
        />
      </div>

      <DetailModal log={detail} onClose={() => setDetail(null)} />

      <Section title="Filter" testid="email-logs-filter-section">
        <div className="flex flex-wrap gap-2 items-end">
          <SelectInput
            data-testid="email-logs-template-filter"
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
          >
            <option value="">All templates</option>
            <option value="team_invitation">team_invitation</option>
            <option value="password_reset">password_reset</option>
            <option value="email_verification">email_verification</option>
          </SelectInput>
          <SelectInput
            data-testid="email-logs-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="sent">sent</option>
            <option value="sandbox">sandbox</option>
            <option value="failed">failed</option>
            <option value="bounced">bounced</option>
          </SelectInput>
          <TextInput
            data-testid="email-logs-search"
            type="email"
            placeholder="Search by recipient…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="min-w-[240px]"
          />
        </div>
      </Section>

      <Section
        title="Emails"
        testid="email-logs-section"
        bodyClassName="p-0"
        badge={
          <span
            className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            {items.length} showing
          </span>
        }
      >
        <div className="overflow-x-auto" data-testid="email-logs-table">
          <table className="w-full text-[13px]">
            <thead>
              <tr
                className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                style={{
                  color: "var(--ds-color-ink-subtle)",
                  background: "var(--ds-color-admin-surface-muted)",
                  borderBottom: "1px solid var(--ds-color-admin-border)",
                }}
              >
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Template</th>
                <th className="px-4 py-2">To</th>
                <th className="px-4 py-2">Subject</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Message ID</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-[13px]"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    No emails match.
                  </td>
                </tr>
              )}
              {items.map((it, idx) => {
                const isLast = idx === items.length - 1;
                return (
                  <tr
                    key={it.id}
                    data-testid={`email-log-row-${it.id}`}
                    className="cursor-pointer transition-colors"
                    onClick={() => setDetail(it)}
                    style={{
                      borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                      transitionDuration: "var(--ds-duration-fast)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td
                      className="px-4 py-1.5 text-[12px] whitespace-nowrap tabular-nums font-mono"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                    </td>
                    <td
                      className="px-4 py-1.5 text-[12px] font-mono"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {it.template_key || it.template_alias || "raw"}
                    </td>
                    <td className="px-4 py-1.5 text-[12px]" style={{ color: "var(--ds-color-ink)" }}>
                      {it.to}
                    </td>
                    <td
                      className="px-4 py-1.5 text-[12px] truncate max-w-[280px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {it.subject}
                    </td>
                    <td className="px-4 py-1.5">
                      <StatusPill status={it.status} />
                    </td>
                    <td
                      className="px-4 py-1.5 text-[10px] font-mono"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {it.message_id ? it.message_id.slice(0, 12) + "…" : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </AdminLayout>
  );
}
